import React, { useState } from 'react';
import { useLeaderboard } from '../../hooks/useLeaderboard';
import { useUserStats } from '../../hooks/useUserStats';
import { useUserGames } from '../../hooks/useUserGames';
import { useGameTypes } from '../../hooks/useGameTypes';
import { useNavigate } from 'react-router-dom';
import GameConfirmationModal from '../GameConfirmationModal/GameConfirmationModal';
import { getGameById } from '../../services/gameService';
import './Statistics.css';

interface GameTypeStats {
  games_played: number;
  total_score: number;
  high_score: number;
}

interface UserGame {
  id: string;
  game_type: string;
  difficulty: string;
  status: string;
  score: number;
  updated_at: string;
  current_prompt?: string;
  completed_at: string | null;
  history?: Array<{
    action: string;
    response: {
      status: string;
      reaction: string;
      feedback: string;
      next_prompt: string;
      score_adjustment: number;
      suggestions: string[];
      response_quality: string;
    };
    timestamp: string;
    score_change: number;
  }>;
}

interface UserStats {
  total_score: number;
  games_played: number;
  high_score: number;
  average_score: number;
  by_game_type: Record<string, GameTypeStats>;
}

const getRandomColor = () => {
  const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEEAD', '#D4A5A5', '#9B89B3'];
  return colors[Math.floor(Math.random() * colors.length)];
};

const formatTimeAgo = (dateString: string | undefined) => {
  if (!dateString) return 'Unknown date';
  try {
    const date = dateString.includes('T') 
      ? new Date(dateString)
      : new Date(dateString + 'Z');

    if (isNaN(date.getTime())) return 'Invalid date';

    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return 'Invalid date';
  }
};

const getStatusClass = (status: string): string => {
  const statusLower = status.toLowerCase();
  if (['critical', 'perilous', 'threatened', 'unstable'].includes(statusLower)) return 'danger';
  if (['advancing', 'progressing', 'triumphant', 'secure'].includes(statusLower)) return 'success';
  if (['stable', 'unchanged', 'maintaining', 'steady'].includes(statusLower)) return 'neutral';
  if (['advantageous', 'promising', 'favorable', 'fortunate'].includes(statusLower)) return 'opportunity';
  if (['challenged', 'hindered', 'complicated', 'troubled'].includes(statusLower)) return 'setback';
  return 'neutral';
};

const getStatusIcon = (status: string): string => {
  const statusClass = getStatusClass(status);
  switch (statusClass) {
    case 'danger':
      return 'lni lni-warning';
    case 'success':
      return 'lni lni-checkmark-circle';
    case 'neutral':
      return 'lni lni-pulse';
    case 'opportunity':
      return 'lni lni-star-filled';
    case 'setback':
      return 'lni lni-flag-alt';
    default:
      return 'lni lni-pulse';
  }
};

const Statistics: React.FC = () => {
  const { leaderboard, isLoading: leaderboardLoading, error: leaderboardError } = useLeaderboard();
  const { stats, isLoading: statsLoading, error: statsError } = useUserStats();
  const { games, isLoading: gamesLoading, error: gamesError } = useUserGames();
  const { gameTypes } = useGameTypes();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedGame, setSelectedGame] = useState<UserGame | null>(null);
  const navigate = useNavigate();

  // Type guard to check if stats is UserStats
  const isUserStats = (stats: any): stats is UserStats => {
    return stats && 
      typeof stats.total_score === 'number' &&
      typeof stats.games_played === 'number' &&
      typeof stats.high_score === 'number' &&
      typeof stats.average_score === 'number' &&
      typeof stats.by_game_type === 'object';
  };

  // Ensure stats is properly typed
  const typedStats = isUserStats(stats) ? stats : null;

  const handleResume = (game: UserGame) => {
    setSelectedGame(game);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedGame(null);
  };

  const handleModalConfirm = async () => {
    if (!selectedGame) return;
    setIsModalOpen(false);
    
    try {
      // Fetch full game details before navigating
      const gameDetails = await getGameById(selectedGame.id);
      
      // Get the last suggestions from history if available
      const lastSuggestions = gameDetails.history && gameDetails.history.length > 0
        ? gameDetails.history[gameDetails.history.length - 1].response.suggestions
        : [];

      navigate(`/game/${selectedGame.game_type}`, {
        state: {
          sessionId: gameDetails.id,
          gameType: gameDetails.game_type,
          difficulty: gameDetails.difficulty,
          initialScenario: gameDetails.current_prompt,
          initialScore: gameDetails.score,
          status: gameDetails.status,
          reaction: gameDetails.reaction,
          initialSuggestions: lastSuggestions,
          history: gameDetails.history, // Pass the complete history
          background_image_url: gameTypes?.[selectedGame.game_type]?.background_image_url
        }
      });
    } catch (error) {
      console.error('Error fetching game details:', error);
      // You might want to show an error message to the user here
    }
  };

  // Get the 15 most recent games
  const getRecentGames = () => {
    if (!games) return [];
    return games.slice(0, 15); // Already sorted by most recent from the backend
  };

  return (
    <div className="stats-container">
      <div className="stats-card">
        <h3>Global High Scores</h3>
        <div className="scores-list">
          {leaderboardLoading && <div className="loading">Loading leaderboard...</div>}
          {leaderboardError && <div className="error">{leaderboardError}</div>}
          {!leaderboardLoading && !leaderboardError && leaderboard?.map((entry, index) => (
            <div key={`${entry.user_email}-${entry.high_score}`} className="score-item">
              <span className="rank">{index + 1}.</span>
              <div className="player-icon">
                <i className="lni lni-user" style={{ color: getRandomColor() }}></i>
              </div>
              <div className="score-info">
                <span className="player-name">{entry.nickname}</span>
                <span className="score">{entry.high_score.toLocaleString()}</span>
              </div>
            </div>
          ))}
          {!leaderboardLoading && !leaderboardError && (!leaderboard || leaderboard.length === 0) && (
            <div className="empty-state">No high scores yet</div>
          )}
        </div>
      </div>

      <div className="stats-card">
        <h3>Your Game Stats</h3>
        {statsLoading && <div className="loading">Loading your stats...</div>}
        {statsError && <div className="error">{statsError}</div>}
        {!statsLoading && !statsError && typedStats && (
          <>
            <div className="scores-list">
              {Object.entries(typedStats.by_game_type).map(([gameType, gameStats]) => {
                const typedGameStats = gameStats as GameTypeStats;
                return (
                  <div key={`${gameType}-${typedGameStats.high_score}`} className="score-item">
                    <span className="game-icon">
                      <i className="lni lni-game" style={{ color: getRandomColor() }}></i>
                    </span>
                    <div className="score-info">
                      <div className="game-details">
                        <span className="game-name">{gameType.charAt(0).toUpperCase() + gameType.slice(1).replace(/_/g, ' ')}</span>
                        <span className="games-played">{typedGameStats.games_played} games</span>
                      </div>
                      <span className="score">{typedGameStats.high_score.toLocaleString()}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="stats-overview">
              <div className="stat-item">
                <span className="stat-label">Total Score</span>
                <span className="stat-value">{typedStats.total_score.toLocaleString()}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Games Played</span>
                <span className="stat-value">{typedStats.games_played}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">High Score</span>
                <span className="stat-value">{typedStats.high_score.toLocaleString()}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Average Score</span>
                <span className="stat-value">{typedStats.average_score.toLocaleString()}</span>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="stats-card">
        <h3>Game History</h3>
        {gamesLoading && <div className="loading">Loading your games...</div>}
        {gamesError && <div className="error">{gamesError}</div>}
        {!gamesLoading && !gamesError && games && (
          <div className="history-list">
            {getRecentGames().map((game) => (
              <div key={game.id} className={`history-item ${game.completed_at ? 'completed' : ''}`}>
                <div className="game-info">
                  <span className="game-name">
                    {game.game_type.charAt(0).toUpperCase() + game.game_type.slice(1).replace(/_/g, ' ')}
                  </span>
                  <div className="game-details">
                    
                    <span className={`game-state`}>
                      <i className={`lni ${game.completed_at ? 'lni-checkmark-circle' : 'lni-timer'}`}></i>
                      {game.completed_at ? 'Completed' : 'Open'}
                    </span>
                    <span className={`game-status ${getStatusClass(game.status)}`}>
                      <i className={getStatusIcon(game.status)}></i>
                      {game.status}
                    </span>
                  </div>
                </div>
                <div className="game-actions">
                  <div className="top-row">
                    <span className={`game-score ${
                      game.score < 0 ? 'negative' : 
                      game.score < 5 ? 'low' : 
                      'high'
                    }`}>
                      {game.score}
                      <i className="lni lni-star"></i>
                    </span>
                  </div>
                  <div className="bottom-row">
                    <span className="timestamp">{formatTimeAgo(game.updated_at)}</span>
                    {!game.completed_at && (
                      <button 
                        className="resume-btn" 
                        title="Resume Game" 
                        onClick={() => handleResume(game)}
                      >
                        <i className="lni lni-play"></i>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {getRecentGames().length === 0 && (
              <div className="empty-state">No games played yet</div>
            )}
          </div>
        )}
      </div>

      {/* Game Confirmation Modal */}
      {selectedGame && (
        <GameConfirmationModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onConfirm={handleModalConfirm}
          gameTitle={selectedGame.game_type.charAt(0).toUpperCase() + selectedGame.game_type.slice(1).replace(/_/g, ' ')}
          gameKey={selectedGame.game_type}
          description={selectedGame.current_prompt || 'Resume your game in progress'}
          difficulty={selectedGame.difficulty}
          isResume={true}
          sessionId={selectedGame.id}
          lastPrompt={selectedGame.current_prompt}
          videoUrl={gameTypes?.[selectedGame.game_type]?.intro_video_url}
          imageUrl={gameTypes?.[selectedGame.game_type]?.image_url}
          backgroundImageUrl={gameTypes?.[selectedGame.game_type]?.background_image_url}
        />
      )}
    </div>
  );
};

export default Statistics; 